import { ApiError, del, get, post, put } from './api';
import type {
  AdminTourAnchor,
  AdminTourBlockingStep,
  AdminTourDetail,
  AdminTourDraftRequest,
  AdminTourDraftState,
  AdminTourFeature,
  AdminTourFieldError,
  AdminTourPlatform,
  AdminTourPlatformCounts,
  AdminTourSettings,
  AdminTourStep,
  AdminTourStepSource,
  AdminTourSummary,
  AdminToursResponse,
  AdminTourWriteFailure,
  AdminTourWriteResult,
} from '../types/adminTours';
import { normalizeTour } from './tours';
import type { Tour, TourKind } from '../types/tours';

const BASE_URL = '/api/admin/tours';

const tourUrl = (key: string) => `${BASE_URL}/${encodeURIComponent(key)}`;

const TOUR_KINDS: readonly TourKind[] = ['Page', 'Data', 'FlowStep'];
const STEP_SOURCES: readonly AdminTourStepSource[] = ['Code', 'Admin'];
const PLATFORMS: readonly AdminTourPlatform[] = ['Web', 'Mobile'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const str = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const nullableStr = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

const bool = (value: unknown, fallback = false): boolean =>
  typeof value === 'boolean' ? value : fallback;

const num = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const array = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const counts = (raw: unknown): AdminTourPlatformCounts => {
  const record = isRecord(raw) ? raw : {};
  return { enabled: num(record.enabled), total: num(record.total) };
};

const normalizeSummary = (raw: unknown): AdminTourSummary | null => {
  if (!isRecord(raw)) return null;
  const key = nullableStr(raw.key);
  if (!key) return null;
  const rawCounts = isRecord(raw.counts) ? raw.counts : {};
  return {
    key,
    kind: TOUR_KINDS.includes(raw.kind as TourKind) ? (raw.kind as TourKind) : 'Page',
    name: str(raw.name, key),
    webRoute: nullableStr(raw.webRoute),
    mobileRoute: nullableStr(raw.mobileRoute),
    isEnabled: bool(raw.isEnabled, true),
    hasDraft: bool(raw.hasDraft),
    resetAt: nullableStr(raw.resetAt),
    removedFromCode: bool(raw.removedFromCode),
    counts: { web: counts(rawCounts.web), mobile: counts(rawCounts.mobile) },
    newFromCodeCount: num(raw.newFromCodeCount),
    removedFromCodeCount: num(raw.removedFromCodeCount),
  };
};

const normalizeFeature = (raw: unknown): AdminTourFeature | null => {
  if (!isRecord(raw)) return null;
  const key = nullableStr(raw.key);
  if (!key) return null;
  return {
    key,
    name: str(raw.name, key),
    tours: array(raw.tours)
      .map(normalizeSummary)
      .filter((tour): tour is AdminTourSummary => tour !== null),
  };
};

/** Guards the list at the boundary: one malformed tour is dropped rather than emptying the page. */
export const normalizeAdminTours = (raw: unknown): AdminToursResponse => {
  if (!isRecord(raw)) return { features: [] };
  return {
    features: array(raw.features)
      .map(normalizeFeature)
      .filter((feature): feature is AdminTourFeature => feature !== null),
  };
};

const normalizeStep = (raw: unknown, platform: AdminTourPlatform): AdminTourStep | null => {
  if (!isRecord(raw)) return null;
  const key = nullableStr(raw.key);
  if (!key) return null;
  const defaultTitle = str(raw.defaultTitle);
  const defaultBody = str(raw.defaultBody);
  return {
    key,
    platform: PLATFORMS.includes(raw.platform as AdminTourPlatform)
      ? (raw.platform as AdminTourPlatform)
      : platform,
    anchorId: nullableStr(raw.anchorId),
    anchorLabel: nullableStr(raw.anchorLabel),
    source: STEP_SOURCES.includes(raw.source as AdminTourStepSource)
      ? (raw.source as AdminTourStepSource)
      : 'Code',
    title: str(raw.title, defaultTitle),
    body: str(raw.body, defaultBody),
    defaultTitle,
    defaultBody,
    hasOverride: bool(raw.hasOverride),
    requiredPermission: nullableStr(raw.requiredPermission),
    sortOrder: num(raw.sortOrder),
    isEnabled: bool(raw.isEnabled, true),
    isNewFromCode: bool(raw.isNewFromCode),
    removedFromCode: bool(raw.removedFromCode),
  };
};

const normalizeSteps = (raw: unknown, platform: AdminTourPlatform): AdminTourStep[] =>
  array(raw)
    .map((step) => normalizeStep(step, platform))
    .filter((step): step is AdminTourStep => step !== null)
    .sort((a, b) => a.sortOrder - b.sortOrder);

export const normalizeAdminTourDetail = (raw: unknown, fallbackKey: string): AdminTourDetail => {
  const record = isRecord(raw) ? raw : {};
  const rawSteps = isRecord(record.steps) ? record.steps : {};
  const key = nullableStr(record.key) ?? fallbackKey;
  return {
    key,
    feature: str(record.feature),
    kind: TOUR_KINDS.includes(record.kind as TourKind) ? (record.kind as TourKind) : 'Page',
    name: str(record.name, key),
    webRoute: nullableStr(record.webRoute),
    mobileRoute: nullableStr(record.mobileRoute),
    isEnabled: bool(record.isEnabled, true),
    resetAt: nullableStr(record.resetAt),
    draftUpdatedAt: nullableStr(record.draftUpdatedAt),
    draftUpdatedByName: nullableStr(record.draftUpdatedByName),
    hasDraft: bool(record.hasDraft),
    steps: {
      web: normalizeSteps(rawSteps.web, 'Web'),
      mobile: normalizeSteps(rawSteps.mobile, 'Mobile'),
    },
    deletedStepKeys: array(record.deletedStepKeys).filter(
      (key): key is string => typeof key === 'string' && key.length > 0
    ),
  };
};

const normalizeAnchor = (raw: unknown): AdminTourAnchor | null => {
  if (!isRecord(raw)) return null;
  const id = nullableStr(raw.id);
  if (!id) return null;
  return { id, label: str(raw.label, id), route: str(raw.route) };
};

const normalizeDraftState = (raw: unknown): AdminTourDraftState => {
  const record = isRecord(raw) ? raw : {};
  return {
    draftUpdatedAt: nullableStr(record.draftUpdatedAt),
    hasDraft: bool(record.hasDraft),
  };
};

const messageOf = (body: unknown, fallback: string): string => {
  if (!isRecord(body)) return fallback;
  const message = nullableStr(body.message);
  return message ?? fallback;
};

const fieldErrorsOf = (body: unknown): AdminTourFieldError[] => {
  if (!isRecord(body)) return [];
  return array(body.errors)
    .map((entry): AdminTourFieldError | null => {
      if (!isRecord(entry)) return null;
      const message = nullableStr(entry.message);
      return message ? { field: str(entry.field), message } : null;
    })
    .filter((entry): entry is AdminTourFieldError => entry !== null);
};

const blockingStepsOf = (body: unknown): AdminTourBlockingStep[] => {
  if (!isRecord(body)) return [];
  return array(body.steps)
    .map((entry): AdminTourBlockingStep | null => {
      if (!isRecord(entry)) return null;
      const key = nullableStr(entry.key);
      return key ? { key, anchorId: nullableStr(entry.anchorId) } : null;
    })
    .filter((entry): entry is AdminTourBlockingStep => entry !== null);
};

/**
 * Turns the statuses the editor has a screen for into a value, and lets everything else throw.
 *
 * A 400 without an `errors` array is still a validation failure, just one the API could only phrase
 * as a sentence; it is surfaced as a message with no per-field attachments rather than swallowed.
 */
const toFailure = (err: unknown, fallback: string): AdminTourWriteFailure | null => {
  if (!(err instanceof ApiError)) return null;
  if (err.statusCode === 409) {
    return { kind: 'conflict', message: messageOf(err.response, err.message || fallback) };
  }
  if (err.statusCode === 400) {
    return {
      kind: 'validation',
      message: messageOf(err.response, err.message || fallback),
      errors: fieldErrorsOf(err.response),
    };
  }
  if (err.statusCode === 422) {
    return {
      kind: 'blocked',
      message: messageOf(err.response, err.message || fallback),
      steps: blockingStepsOf(err.response),
    };
  }
  return null;
};

const write = async <T>(
  run: () => Promise<T>,
  fallback: string
): Promise<AdminTourWriteResult<T>> => {
  try {
    return { ok: true, value: await run() };
  } catch (err) {
    const failure = toFailure(err, fallback);
    if (failure) return { ok: false, failure };
    throw err;
  }
};

/** Every tour, grouped by feature. Throws `ApiError`; a 404 means an API without the builder. */
export async function getAdminTours(signal?: AbortSignal): Promise<AdminToursResponse> {
  return normalizeAdminTours(await get<unknown>(BASE_URL, { signal }));
}

/** One tour with both platforms' steps. Throws `ApiError`; a 404 means an API without the builder. */
export async function getAdminTour(key: string, signal?: AbortSignal): Promise<AdminTourDetail> {
  return normalizeAdminTourDetail(await get<unknown>(tourUrl(key), { signal }), key);
}

/** Every element a step on this platform can point at. Throws `ApiError`. */
export async function getTourAnchors(
  platform: AdminTourPlatform,
  signal?: AbortSignal
): Promise<AdminTourAnchor[]> {
  const raw = await get<unknown>(`${BASE_URL}/anchors?platform=${platform}`, { signal });
  return array(isRecord(raw) ? raw.anchors : raw)
    .map(normalizeAnchor)
    .filter((anchor): anchor is AdminTourAnchor => anchor !== null);
}

export async function saveTourDraft(
  key: string,
  body: AdminTourDraftRequest,
  signal?: AbortSignal
): Promise<AdminTourWriteResult<AdminTourDraftState>> {
  return write(
    async () => normalizeDraftState(await put<unknown>(`${tourUrl(key)}/draft`, body, { signal })),
    'The draft could not be saved.'
  );
}

export async function discardTourDraft(
  key: string,
  expectedDraftUpdatedAt: string | null,
  signal?: AbortSignal
): Promise<AdminTourWriteResult<AdminTourDraftState>> {
  return write(
    async () =>
      normalizeDraftState(
        await del<unknown>(`${tourUrl(key)}/draft`, {
          signal,
          body: JSON.stringify({ expectedDraftUpdatedAt }),
        })
      ),
    'The draft could not be discarded.'
  );
}

export async function publishTour(
  key: string,
  expectedDraftUpdatedAt: string | null,
  signal?: AbortSignal
): Promise<AdminTourWriteResult<AdminTourDetail>> {
  return write(
    async () =>
      normalizeAdminTourDetail(
        await post<unknown>(`${tourUrl(key)}/publish`, { expectedDraftUpdatedAt }, { signal }),
        key
      ),
    'The tour could not be published.'
  );
}

/**
 * One tour with its draft applied, in the same shape `GET /api/tours/me` serves.
 *
 * Throws `ApiError` when the API refuses (a 404 for a tour that does not exist, a 403 for anyone
 * who is not a system admin) or the request fails. Null only when it answers with something that is
 * not a tour. Both reach the admin who asked: `TourProvider` shows a notice saying the draft could
 * not be loaded, or that it has no step this page can show, rather than doing nothing.
 */
export async function getTourPreview(
  key: string,
  platform: AdminTourPlatform = 'Web',
  signal?: AbortSignal
): Promise<Tour | null> {
  const raw = await get<unknown>(`${tourUrl(key)}/preview?platform=${platform}`, { signal });
  return normalizeTour(raw);
}

/** Re-shows the tour to everyone who has already seen it. */
export async function resetTour(
  key: string,
  signal?: AbortSignal
): Promise<{ resetAt: string | null }> {
  const raw = await post<unknown>(`${tourUrl(key)}/reset`, undefined, { signal });
  return { resetAt: isRecord(raw) ? nullableStr(raw.resetAt) : null };
}

const SETTINGS_URL = `${BASE_URL}/settings`;

/**
 * Off unless the API says otherwise. A missing or malformed flag must never read as "on", since
 * that would tell an admin coaches can see tours when they cannot.
 */
export const normalizeTourSettings = (raw: unknown): AdminTourSettings => {
  const record = isRecord(raw) ? raw : {};
  return {
    enabledForEveryone: bool(record.enabledForEveryone),
    updatedAt: nullableStr(record.updatedAt),
  };
};

/** Whether coaches see tours, or only system admins. Throws `ApiError`; a 404 means an older API. */
export async function getTourSettings(signal?: AbortSignal): Promise<AdminTourSettings> {
  return normalizeTourSettings(await get<unknown>(SETTINGS_URL, { signal }));
}

/** Flips the global switch. Throws `ApiError`. */
export async function updateTourSettings(
  enabledForEveryone: boolean,
  signal?: AbortSignal
): Promise<AdminTourSettings> {
  return normalizeTourSettings(await put<unknown>(SETTINGS_URL, { enabledForEveryone }, { signal }));
}
